import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  HostBinding,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { TOUR_PATH } from '@cdw-ae/common-data';
import { CdwIconModule } from '@cdw-ae/common-ui-components-icon';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { environment } from '../../../../../environments/environment';
import { inTestingFeaturesEnabled } from '../../../../common/guards/in-testing-feature.guard';
import { JobIndicatorComponent } from './job-indicator/job-indicator.component';

export interface NavItem {
  title: string;
  icon?: string; // Optional, but required for level 1
  route?: string; // Mutually exclusive with children
  children?: NavItem[];
  isExpanded?: boolean; // State for items with children
  externalLink?: boolean; // Flag for external links
  isActive?: boolean;
}

export type SidebarState = 'collapsed' | 'expanded' | 'peeking';

/**
 * The Support accordion (Help Center + Contact Support), shared by the app
 * sidebar, the onboarding rail, and the login-status mock backdrop. A FACTORY,
 * not a shared constant, because nav items are mutated at runtime
 * (isExpanded/isActive) — a single shared object would leak that state between
 * the three rails. Each caller renders its own fresh copy. Help Center uses the
 * declared environment URL; the support mailto lives here as its single source of
 * truth.
 */
export function supportNavItem(
  options: {
    /**
     * Add "Take a Tour" (EDUS-2693). Opt-IN because `/tour` sits behind
     * authGuard: the onboarding rail shares this factory, and an admin
     * mid-wizard holds only a minimal onboarding token — clicking it there
     * would bounce them to /welcome and cost them their session.
     */
    includeTour?: boolean;
  } = {}
): NavItem {
  return {
    title: 'Support',
    icon: 'support',
    isExpanded: false,
    children: [
      {
        title: 'Help Center',
        icon: 'help_outline',
        route: environment.urls.support.helpCenter,
        externalLink: true,
      },
      {
        title: 'Contact Support',
        icon: 'support_agent',
        route: environment.urls.support.contactSupport,
        externalLink: true,
      },
      // An in-app route, NOT an external link. This is also the "return to
      // this screen at any time from Support" the tour page's own copy
      // promises (EDUS-2852), so removing it would make that copy a lie.
      ...(options.includeTour
        ? [
            {
              title: 'Take a Tour',
              icon: 'tour',
              route: TOUR_PATH,
            },
          ]
        : []),
      // Hidden for now, deprecate or bring back in later phases
      // { title: 'Feedback', icon: 'feedback', route: '/feedback' },
      // { title: 'Team Credits', icon: 'groups', route: '/team-credits' },
    ],
  };
}

const SIDEBAR_EXPANDED_STORAGE_KEY = 'spog-sidebar-expanded';
const PEEK_HOVER_LEAVE_GRACE_MS = 200;

@Component({
  selector: 'app-sidebar-navigation',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    CdwIconModule,
    JobIndicatorComponent,
    MatTooltipModule,
  ],
  templateUrl: './sidebar-navigation.component.html',
  styleUrls: ['./sidebar-navigation.component.scss'],
})
export class SidebarNavigationComponent implements OnInit, OnDestroy {
  sidebarState: SidebarState = 'collapsed';

  @HostBinding('class.is-collapsed') get isCollapsedHost(): boolean {
    return this.sidebarState === 'collapsed';
  }
  @HostBinding('class.is-expanded') get isExpandedHost(): boolean {
    return this.sidebarState === 'expanded';
  }
  @HostBinding('class.is-peeking') get isPeekingHost(): boolean {
    return this.sidebarState === 'peeking';
  }

  @Input() userInfo: { name: string; avatarUrl: string } | null = {
    name: 'Admin User',
    avatarUrl: 'assets/placeholder-avatar.png',
  };

  @Input() jobsPanelOpen = false;
  @Output() jobsPanelToggle = new EventEmitter<void>();

  /** Whether the bottom-left active-jobs indicator is shown (hidden in onboarding). */
  @Input() showJobIndicator = true;

  /**
   * Account footer behavior:
   * - 'profile' (default): links to the in-app /account-profile page.
   * - 'logout': avatar opens a menu with the user's email + Sign out (used in
   *   onboarding, where in-app routes aren't reachable). Emits {@link signOut}.
   */
  @Input() accountMode: 'profile' | 'logout' = 'profile';
  @Input() accountEmail: string | null = null;
  @Input() accountPhotoUrl: string | null = null;
  @Input() accountInitial = '?';
  @Output() signOut = new EventEmitter<void>();

  protected accountMenuOpen = false;

  /**
   * The inline array literal is load-bearing: it must stay per-instance because
   * updateActiveState() and syncExpandedToActiveRoute() mutate these objects at
   * runtime. Three components now render this rail (internal layout, onboarding
   * wizard, and login-status mock backdrop). If someone promotes the default to a
   * module-level constant, those three will start corrupting each other's state.
   */
  @Input() navigationItems: NavItem[] = [
    { title: 'Dashboard', icon: 'pie_chart', route: '/dashboard' },
    {
      title: 'Device Search',
      icon: 'screen_search_desktop',
      route: '/deviceSearch',
    },
    { title: 'Reports', icon: 'multiline_chart', route: '/reports' },
    {
      title: 'Reports',
      icon: 'bar_chart',
      isExpanded: false,
    },
    //  Hidden for now, deprecate or bring back in later phases
    // {
    //   title: 'Jobs',
    //   icon: 'description',
    //   route: '/jobs',
    // children: [
    //   {
    //     title: 'Devices',
    //     icon: 'devices',
    //     isExpanded: false,
    //     children: [
    //       {
    //         title: 'Profile Wipe',
    //         route: '/jobs/devices/chrome-profile-wipe',
    //       },
    //       { title: 'Lock Down', route: '/jobs/devices/lock-down' },
    //     ],
    //   },
    //   {
    //     title: 'Users',
    //     icon: 'group',
    //     isExpanded: false,
    //     children: [
    //       { title: 'Change Status', route: '/jobs/users/buld-status' },
    //       { title: 'Update Attributes', route: '/jobs/users/user-schema' },
    //     ],
    //   },
    // ],
    // },

    {
      title: 'Administration',
      icon: 'settings',
      children: [
        // EDUS-2576: Access management is still in active testing, so it is only
        // shown in non-customer-facing environments (see inTestingFeaturesEnabled).
        ...(inTestingFeaturesEnabled
          ? [
              {
                title: 'Access management',
                icon: 'manage_accounts',
                route: 'administration/access-management',
              },
            ]
          : []),
        // { title: 'Team permissions', icon: 'groups', route: 'administration/team-permissions' },
        {
          title: 'Audit logs',
          icon: 'policy',
          route: 'administration/audit-log',
        },
        {
          title: 'Global settings',
          icon: 'settings_applications',
          route: 'administration/global-settings',
        },
      ],
    },

    supportNavItem({ includeTour: true }),
  ];

  private activeUrl = '';
  private routerSubscription!: Subscription;
  private peekCloseTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(private router: Router) {}

  ngOnInit(): void {
    if (this.readExpandedFromStorage()) {
      this.sidebarState = 'expanded';
    }

    this.routerSubscription = this.router.events
      .pipe(
        filter(
          (event): event is NavigationEnd => event instanceof NavigationEnd
        )
      )
      .subscribe((event: NavigationEnd) => {
        this.activeUrl = event.urlAfterRedirects;
        this.updateActiveState();
        // Navigation is the only event that should auto-mutate which parent
        // is expanded. State transitions (toggle, peek, pin) preserve the
        // user's explicit choice.
        this.syncExpandedToActiveRoute();
      });

    this.activeUrl = this.router.url;
    this.updateActiveState();
    this.syncExpandedToActiveRoute();
  }

  ngOnDestroy(): void {
    this.routerSubscription?.unsubscribe();
    this.clearPeekCloseTimer();
  }

  /**
   * Derives a stable data-testid for an external nav link from its title
   * (e.g. 'Help Center' → 'sidebar-help-center-link'). Mirrors the
   * `'navbar-action-' + button.id` pattern in NavbarActionButtons — keeps
   * test concerns out of the NavItem domain interface.
   */
  protected sidebarTestId(item: NavItem): string {
    return `sidebar-${item.title.toLowerCase().replace(/\s+/g, '-')}-link`;
  }

  /**
   * Returns the icon to render in the arrow button for the current state.
   */
  get arrowIcon(): string {
    return this.sidebarState === 'collapsed'
      ? 'left_panel_open'
      : 'left_panel_close';
  }

  /**
   * Arrow click behavior:
   * - collapsed → expanded (persisted)
   * - expanded → collapsed (persistence cleared)
   * - peeking  → expanded (pin the peek; persisted)
   */
  onArrowClick(): void {
    this.clearPeekCloseTimer();
    if (this.sidebarState === 'expanded') {
      this.writeExpandedToStorage(false);
      this.setState('collapsed');
    } else {
      this.writeExpandedToStorage(true);
      this.setState('expanded');
    }
  }

  /**
   * Click on an expandable parent (item with children):
   * - collapsed: enter PEEK with this parent expanded
   * - expanded/peeking: just toggle this parent's children (mutual exclusion)
   */
  toggleItemExpansion(item: NavItem): void {
    if (!item.children) {
      return;
    }

    if (this.sidebarState === 'collapsed') {
      this.collapseAllTopLevelItems(item);
      item.isExpanded = true;
      this.setState('peeking');
      return;
    }

    const wasExpanded = item.isExpanded;
    this.collapseAllTopLevelItems(item);
    item.isExpanded = !wasExpanded;
  }

  /**
   * Click on a leaf navigation item: dismisses the peek if currently peeking,
   * leaves the persistent expanded state alone otherwise.
   */
  onLeafItemClick(): void {
    if (this.sidebarState === 'peeking') {
      this.clearPeekCloseTimer();
      this.setState('collapsed');
    }
  }

  // Account footer menu (logout mode only) --------------------------------------
  protected toggleAccountMenu(): void {
    this.accountMenuOpen = !this.accountMenuOpen;
  }

  protected closeAccountMenu(): void {
    this.accountMenuOpen = false;
  }

  protected onSignOut(): void {
    this.closeAccountMenu();
    this.signOut.emit();
  }

  onMouseEnter(): void {
    // Cancel any pending peek-close timer if the user re-enters the sidebar
    // before the grace period elapses.
    this.clearPeekCloseTimer();
  }

  onMouseLeave(): void {
    if (this.sidebarState !== 'peeking') {
      return;
    }
    this.clearPeekCloseTimer();
    this.peekCloseTimer = setTimeout(() => {
      this.peekCloseTimer = null;
      if (this.sidebarState === 'peeking') {
        this.setState('collapsed');
      }
    }, PEEK_HOVER_LEAVE_GRACE_MS);
  }

  private setState(next: SidebarState): void {
    if (this.sidebarState === next) {
      return;
    }
    this.sidebarState = next;
    if (next === 'collapsed') {
      this.collapseAllItems(this.navigationItems);
    }
    this.updateActiveState();
  }

  private clearPeekCloseTimer(): void {
    if (this.peekCloseTimer !== null) {
      clearTimeout(this.peekCloseTimer);
      this.peekCloseTimer = null;
    }
  }

  private readExpandedFromStorage(): boolean {
    try {
      return localStorage.getItem(SIDEBAR_EXPANDED_STORAGE_KEY) === 'true';
    } catch {
      return false;
    }
  }

  private writeExpandedToStorage(expanded: boolean): void {
    try {
      if (expanded) {
        localStorage.setItem(SIDEBAR_EXPANDED_STORAGE_KEY, 'true');
      } else {
        localStorage.removeItem(SIDEBAR_EXPANDED_STORAGE_KEY);
      }
    } catch {
      // localStorage may be unavailable (private browsing, quota); silently ignore.
    }
  }

  /**
   * Walk navigationItems and return the path (array of items from top-level
   * to leaf) that best matches the current activeUrl. Empty array if none.
   */
  private findActiveBestMatch(): NavItem[] {
    let bestMatch: NavItem[] = [];

    const findPath = (
      items: NavItem[],
      currentPath: NavItem[],
      url: string
    ): void => {
      for (const item of items) {
        const newPath = [...currentPath, item];
        if (item.route && !item.externalLink && url.endsWith(item.route)) {
          if (newPath.length > bestMatch.length) {
            bestMatch = newPath;
          }
        }

        if (item.children) {
          findPath(item.children, newPath, url);
        }
      }
    };

    findPath(this.navigationItems, [], this.activeUrl);
    return bestMatch;
  }

  /**
   * Updates `isActive` flags on nav items based on the current active route.
   * Does NOT mutate `isExpanded` — that's the user's job (or a navigation event).
   */
  private updateActiveState(): void {
    const resetActive = (items: NavItem[]) => {
      for (const item of items) {
        item.isActive = false;
        if (item.children) {
          resetActive(item.children);
        }
      }
    };
    resetActive(this.navigationItems);

    const bestMatch = this.findActiveBestMatch();
    if (bestMatch.length === 0) {
      return;
    }

    if (this.sidebarState !== 'collapsed') {
      // When expanded or peeking, only the exact leaf is active
      bestMatch[bestMatch.length - 1].isActive = true;
    } else {
      // When collapsed, highlight the top-level parent of the match
      bestMatch[0].isActive = true;
    }
  }

  /**
   * Auto-expands the top-level parent of the currently-active route, with
   * mutual exclusion (other parents collapse). Called only on init and on
   * actual route navigation — NOT on every state transition, otherwise
   * peeking at a different parent would fight against the user's intent.
   */
  private syncExpandedToActiveRoute(): void {
    if (this.sidebarState === 'collapsed') {
      return;
    }
    const bestMatch = this.findActiveBestMatch();
    if (bestMatch.length < 2) {
      return; // route is a top-level leaf with no parent to expand
    }
    this.collapseAllTopLevelItems(bestMatch[0]);
    bestMatch[0].isExpanded = true;
  }

  /**
   * Recursively collapses all expandable navigation items.
   */
  private collapseAllItems(items: NavItem[]): void {
    for (const item of items) {
      if (item.children && item.children.length > 0) {
        item.isExpanded = false;
        this.collapseAllItems(item.children); // Recursive call
      }
    }
  }

  /**
   * Collapses all top-level accordion items except the specified one.
   */
  private collapseAllTopLevelItems(exceptItem?: NavItem): void {
    for (const item of this.navigationItems) {
      if (item.children && item !== exceptItem) {
        item.isExpanded = false;
        this.collapseAllItems(item.children);
      }
    }
  }
}
